/* ------------------------------------------------------------
   BERIT × Klaviyo
   Sends waitlist sign-ups to Klaviyo straight from the browser,
   using Klaviyo's public Client API (safe to call client-side
   with the public key; no secrets live in this file).

   One call, verified against Klaviyo's API spec (rev 2026-04-15):
     - subscribe()   client/subscriptions — joins the BERIT Waitlist
                     list with email marketing consent and custom
                     profile properties: the visitor's country
                     (country_code) always, plus the selected
                     variant (variant_id) from the product page.
                     Joining the list is what triggers the
                     confirmation flow in Klaviyo. Restock emails
                     are sent by segmenting on variant_id (there is
                     no automatic back-in-stock trigger).

   Configuration (public key, list ID, country) is read from data
   attributes on the footer sign-up block, which is present on every
   page. The values are set in the theme editor under Theme settings
   → BERIT — Klaviyo.

   This file also wires the footer form itself. The product page
   waitlist is wired in berit-product.js, which calls into
   window.BeritKlaviyo.
   ------------------------------------------------------------ */
(function () {
  'use strict';

  var REVISION = '2026-04-15';
  var ENDPOINT = 'https://a.klaviyo.com';

  function configEl() {
    return document.querySelector('[data-berit-klaviyo]');
  }

  function config() {
    var el = configEl();
    return {
      key: el ? (el.getAttribute('data-klaviyo-key') || '').trim() : '',
      listId: el ? (el.getAttribute('data-klaviyo-list') || '').trim() : '',
      country: el ? (el.getAttribute('data-klaviyo-country') || '').trim() : '',
    };
  }

  /* The visitor's two-letter country code, best source first:
     1. Shopify's localisation (rendered into the data attribute)
     2. Shopify's global JS object
     3. The browser's language region (e.g. 'en-AU' -> 'AU') */
  function countryCode() {
    var c = config().country;
    if (!c && window.Shopify && typeof window.Shopify.country === 'string') {
      c = window.Shopify.country;
    }
    if (!c && navigator.language && navigator.language.indexOf('-') !== -1) {
      c = navigator.language.split('-').pop();
    }
    c = (c || '').toUpperCase();
    return /^[A-Z]{2}$/.test(c) ? c : '';
  }

  function validEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());
  }

  function post(path, payload) {
    var key = config().key;
    if (!key) {
      return Promise.reject(new Error('Klaviyo public key is not set in Theme settings.'));
    }
    return fetch(ENDPOINT + path + '?company_id=' + encodeURIComponent(key), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: REVISION,
      },
      body: JSON.stringify(payload),
    }).then(function (res) {
      /* Klaviyo answers 202 with an empty body on success. */
      if (!res.ok) {
        return res.text().then(function (text) {
          throw new Error('Klaviyo ' + res.status + ': ' + text);
        });
      }
    });
  }

  /* Join the waitlist list, with consent, the country property, and any
     extra custom properties (e.g. the product page passes variant_id). */
  function subscribe(email, source, extraProperties) {
    var cfg = config();
    if (!cfg.listId) {
      return Promise.reject(new Error('Klaviyo list ID is not set in Theme settings.'));
    }

    var profileAttributes = {
      email: email.trim(),
      subscriptions: {
        email: { marketing: { consent: 'SUBSCRIBED' } },
      },
    };
    var properties = {};
    var country = countryCode();
    if (country) {
      properties.country_code = country;
    }
    Object.keys(extraProperties || {}).forEach(function (key) {
      var value = (extraProperties || {})[key];
      if (value !== undefined && value !== null && value !== '') {
        properties[key] = value;
      }
    });
    if (Object.keys(properties).length) {
      profileAttributes.properties = properties;
    }

    return post('/client/subscriptions', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source || 'BERIT waitlist',
          profile: {
            data: { type: 'profile', attributes: profileAttributes },
          },
        },
        relationships: {
          list: { data: { type: 'list', id: cfg.listId } },
        },
      },
    });
  }

  window.BeritKlaviyo = {
    subscribe: subscribe,
    validEmail: validEmail,
    countryCode: countryCode,
  };

  /* ---------- Footer sign-up form ---------- */
  function initFooterForm() {
    var wrap = configEl();
    if (!wrap) return;
    var form = wrap.querySelector('[data-berit-klaviyo-form]');
    if (!form || form.dataset.beritReady) return;
    form.dataset.beritReady = 'true';

    var input = form.querySelector('input[type="email"]');
    var noteSuccess = wrap.querySelector('[data-berit-note-success]');
    var noteInvalid = wrap.querySelector('[data-berit-note-invalid]');
    var noteError = wrap.querySelector('[data-berit-note-error]');

    function show(note) {
      [noteSuccess, noteInvalid, noteError].forEach(function (n) {
        if (n) n.hidden = n !== note;
      });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (!input || !validEmail(input.value)) {
        show(noteInvalid);
        return;
      }
      subscribe(input.value, 'BERIT footer sign-up')
        .then(function () {
          /* Swap the spent field and consent line for the thank-you note,
             echoing the typed address so typos are caught on sight. */
          form.querySelectorAll('[data-berit-hide-on-success]').forEach(function (el) {
            el.hidden = true;
          });
          var slot = noteSuccess ? noteSuccess.querySelector('[data-berit-email]') : null;
          if (slot) slot.textContent = input.value.trim();
          show(noteSuccess);
        })
        .catch(function (error) {
          console.error('[berit-klaviyo]', error);
          show(noteError);
        });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFooterForm);
  } else {
    initFooterForm();
  }
})();
